<template>
  <HrPageLayout
    title="Training Management"
    subtitle="Manage training programs, schedules and enrollments"
    icon="🎓"
    :breadcrumb-items="breadcrumbItems"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="$router.push('/trainings-catalog')">
        <i class="bx bx-book"></i> Trainings Catalog
      </button>
    </template>

    <div class="training-management-content">
      <div class="hr-content-grid grid-cards">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-book-reader"></i> Training Programs</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">View and manage all training courses in the catalog.</p>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="$router.push('/trainings-catalog')">Open Catalog</button>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-group"></i> Employee Certifications</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">Record and track employee certifications.</p>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="$router.push('/employee-certifications')">Manage Certifications</button>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-bar-chart"></i> Training Stats</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-d-flex hr-justify-between hr-mb-2"><span class="hr-text-muted">Total</span><strong>{{ trainings.length }}</strong></div>
            <div class="hr-d-flex hr-justify-between"><span class="hr-text-muted">Active</span><strong>{{ activeCount }}</strong></div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-file-blank"></i> Training report submissions</h5>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="loadTrainingReports">
            <i class="bx bx-refresh" :class="{ 'bx-spin': reportsLoading }"></i> Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <p class="hr-text-muted hr-mb-2">
            Reports submitted from <strong>Employee Self-Service → My Training</strong> (Training Report Form). “To be submitted to” comes from configured recipients in the database.
          </p>
          <div v-if="reportsLoading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading reports…</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Officer</th>
                  <th>Emp / PF</th>
                  <th>Training title</th>
                  <th>Submitted to</th>
                  <th>Dates</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in trainingReports" :key="r.id">
                  <td>{{ r.created_at || "—" }}</td>
                  <td>{{ r.officer_name }}</td>
                  <td>{{ r.emp_no }}</td>
                  <td>{{ r.training_title }}</td>
                  <td>{{ r.submit_to_label || r.submit_target_label || "—" }}</td>
                  <td>{{ r.date_from || "—" }} → {{ r.date_to || "—" }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ r.status }}</span></td>
                </tr>
                <tr v-if="trainingReports.length === 0">
                  <td colspan="7" class="hr-text-center hr-py-4 hr-text-muted">No training report submissions yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">All Trainings</h5>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="loadTrainings">
            <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading...</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Type</th><th>Provider</th><th>Duration</th><th>Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="t in trainings" :key="t.trainingID">
                  <td>{{ t.trainingCode }}</td>
                  <td>{{ t.trainingName }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ t.trainingType }}</span></td>
                  <td>{{ t.provider || '—' }}</td>
                  <td>{{ t.duration_days ? t.duration_days + 'd' : '' }} {{ t.duration_hours ? t.duration_hours + 'h' : '—' }}</td>
                  <td><span class="hr-badge" :class="t.is_active == 1 ? 'hr-badge-success' : 'hr-badge-secondary'">{{ t.is_active == 1 ? 'Active' : 'Inactive' }}</span></td>
                </tr>
                <tr v-if="trainings.length === 0">
                  <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No trainings found. Add some in the Trainings Catalog.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "TrainingManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      reportsLoading: false,
      trainings: [],
      trainingReports: [],
      breadcrumbItems: [
        { label: "Dashboard", route: "/dashboard" },
        { label: "Training & Development" },
        { label: "Training Management" }
      ]
    };
  },
  computed: {
    activeCount() {
      return this.trainings.filter(t => t.is_active == 1).length;
    }
  },
  mounted() {
    this.loadTrainings();
    this.loadTrainingReports();
  },
  methods: {
    async loadTrainingReports() {
      this.reportsLoading = true;
      const data = new FormData();
      data.append("function", "listTrainingReportSubmissions");
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.trainingReports = Array.isArray(d?.reports) ? d.reports : [];
      } catch (_) {
        this.trainingReports = [];
      } finally {
        this.reportsLoading = false;
      }
    },
    async loadTrainings() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getTrainings");
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.trainings = Array.isArray(d?.trainings) ? d.trainings : [];
      } catch (_) {
        this.trainings = [];
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.training-management-content { padding: 0; }
.grid-cards { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
</style>
